/**
 * @module FrameworkContext
 * @description Contexto global para la selección y gestión de marcos normativos de cumplimiento.
 *
 * Gestiona los frameworks disponibles en el sistema (ISO 27001, ISO 27002, LMCS, CIS Controls)
 * y expone el framework actualmente activo, que actúa como filtro global en dashboards,
 * métricas y reportes.
 *
 * Comportamiento de inicialización:
 * - Intenta cargar los frameworks desde la tabla `compliance_frameworks` en Supabase.
 * - Si la tabla no existe o la consulta falla, usa los datos mock locales (modo demo).
 * - Los frameworks locales (ej. CIS) siempre se fusionan con los de BD para garantizar
 *   disponibilidad aunque la migración aún no se haya ejecutado.
 * - El framework inicial siempre es ISO 27002; cambios posteriores se persisten en localStorage.
 *
 * @author Sistema ACSGC
 * @version 1.0.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
import type { ComplianceFramework } from '../types/iso';

// =====================================================
// DATOS MOCK PARA MODO DEMO
// =====================================================

/**
 * Frameworks normativos predefinidos usados en modo demo o como fallback
 * cuando la BD no está disponible o aún no tiene la tabla `compliance_frameworks`.
 * Representan los cuatro marcos normativos soportados por el sistema ACSGC.
 */
const MOCK_FRAMEWORKS: ComplianceFramework[] = [
  {
    id: 'fw-iso27002',
    code: 'ISO27002',
    name: 'ISO 27002:2022',
    version: '2022',
    description: 'Controles de seguridad de la información - Guía de implementación con 93 controles organizados en 4 temas.',
    icon: 'Shield',
    color: 'blue',
    country: undefined,
    isActive: true,
    isDefault: true,
    totalControls: 93,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'fw-iso27001',
    code: 'ISO27001',
    name: 'ISO 27001:2022',
    version: '2022',
    description: 'Sistema de gestión de seguridad de la información (SGSI) - Requisitos con controles del Anexo A.',
    icon: 'ShieldCheck',
    color: 'indigo',
    country: undefined,
    isActive: true,
    isDefault: false,
    totalControls: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'fw-lmcs',
    code: 'LMCS',
    name: 'Ley Marco de Ciberseguridad',
    version: '2024',
    description: 'Ley 21.663 - Marco regulatorio de ciberseguridad para Chile con requisitos específicos para infraestructura crítica.',
    icon: 'Scale',
    color: 'red',
    country: 'CL',
    isActive: true,
    isDefault: false,
    totalControls: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'fw-cis',
    code: 'CIS',
    name: 'CIS Controls v8',
    version: '8.0',
    description: 'Center for Internet Security Controls v8 — 18 controles y 153 salvaguardas organizadas en 3 Grupos de Implementación.',
    icon: 'ShieldCheck',
    color: 'orange',
    country: undefined,
    isActive: true,
    isDefault: false,
    totalControls: 153,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

// =====================================================
// INTERFACES Y TIPOS
// =====================================================

/**
 * Contrato del contexto de frameworks normativos.
 *
 * @property frameworks - Lista de todos los frameworks disponibles y activos.
 * @property activeFramework - Framework actualmente seleccionado por el usuario (null durante carga inicial).
 * @property isLoading - `true` mientras se cargan los frameworks desde Supabase.
 * @property error - Mensaje de error si la carga falla; null si no hay error.
 * @property isDemoMode - `true` si se están usando datos mock en lugar de BD real.
 * @property setActiveFramework - Cambia el framework activo por ID; persiste en localStorage.
 * @property refreshFrameworks - Recarga los frameworks desde Supabase.
 * @property getFrameworkByCode - Busca un framework por su código corto (ej. 'ISO27002').
 */
interface FrameworkContextType {
  frameworks: ComplianceFramework[];
  activeFramework: ComplianceFramework | null;
  isLoading: boolean;
  error: string | null;
  isDemoMode: boolean;
  setActiveFramework: (frameworkId: string) => void;
  refreshFrameworks: () => Promise<void>;
  getFrameworkByCode: (code: string) => ComplianceFramework | undefined;
}

/** Contexto interno — se accede mediante el hook `useFramework()`. */
const FrameworkContext = createContext<FrameworkContextType | undefined>(undefined);

// =====================================================
// PROVIDER
// =====================================================

interface FrameworkProviderProps {
  children: React.ReactNode;
}

/**
 * Proveedor del contexto de frameworks normativos.
 *
 * Gestiona la carga, persistencia y selección del framework activo.
 * Debe ubicarse en el árbol de componentes por encima de cualquier dashboard
 * o componente que consuma `useFramework()`.
 *
 * @param children - Árbol de componentes React a envolver.
 */
export function FrameworkProvider({ children }: FrameworkProviderProps) {
  const [frameworks, setFrameworks] = useState<ComplianceFramework[]>([]);
  const [activeFramework, setActiveFrameworkState] = useState<ComplianceFramework | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  /**
   * Carga todos los frameworks activos desde Supabase.
   *
   * Estrategia de fusión: combina los frameworks de BD con los locales (MOCK_FRAMEWORKS)
   * para garantizar que los frameworks aún no migrados estén siempre disponibles.
   *
   * En caso de error, activa el modo demo con MOCK_FRAMEWORKS como fallback.
   */
  const loadFrameworks = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      logger.info('[FRAMEWORK] Cargando frameworks desde BD...');

      const { data, error: fetchError } = await supabase
        .from('compliance_frameworks')
        .select('*')
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('name');

      if (fetchError) {
        throw fetchError;
      }

      if (data && data.length > 0) {
        // Transformar datos de BD (snake_case) a interface TypeScript (camelCase)
        const transformedFrameworks: ComplianceFramework[] = data.map((fw) => ({
          id: fw.id,
          code: fw.code,
          name: fw.name,
          version: fw.version,
          description: fw.description,
          icon: fw.icon,
          color: fw.color || 'blue',
          country: fw.country,
          isActive: fw.is_active,
          isDefault: fw.is_default,
          totalControls: fw.total_controls || 0,
          metadata: fw.metadata,
          createdAt: fw.created_at,
          updatedAt: fw.updated_at
        }));

        // Asegurar que los frameworks locales (ej. CIS) siempre estén disponibles
        // aunque no estén en la BD todavía (antes de ejecutar la migración)
        const localCodes = transformedFrameworks.map(fw => fw.code);
        const mergedFrameworks = [
          ...transformedFrameworks,
          ...MOCK_FRAMEWORKS.filter(fw => !localCodes.includes(fw.code))
        ];

        setFrameworks(mergedFrameworks);
        setIsDemoMode(false);

        // Siempre seleccionar ISO 27002 como default (ignorar localStorage inicial)
        if (!activeFramework) {
          const iso27002Fw = transformedFrameworks.find(fw => fw.code === 'ISO27002')
            || transformedFrameworks.find(fw => fw.isDefault)
            || transformedFrameworks[0];
          setActiveFrameworkState(iso27002Fw);
          logger.info(`[FRAMEWORK] Framework activo (default): ${iso27002Fw.name}`);
        }

        logger.info(`[FRAMEWORK] ${transformedFrameworks.length} frameworks cargados`);
      } else {
        throw new Error('No se encontraron frameworks');
      }
    } catch (err) {
      logger.warn('[FRAMEWORK] Error cargando desde BD, usando modo demo:', err);
      setFrameworks(MOCK_FRAMEWORKS);
      setIsDemoMode(true);

      // Siempre seleccionar ISO 27002 en modo demo
      if (!activeFramework) {
        const iso27002Fw = MOCK_FRAMEWORKS.find(fw => fw.code === 'ISO27002') || MOCK_FRAMEWORKS[0];
        setActiveFrameworkState(iso27002Fw);
        logger.info(`[FRAMEWORK] Framework activo (demo): ${iso27002Fw.name}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [activeFramework]);

  /** Carga inicial de frameworks al montar el proveedor. */
  useEffect(() => {
    loadFrameworks();
  }, [loadFrameworks]);

  /**
   * Persiste el framework activo en localStorage cada vez que cambia,
   * para restaurarlo en la siguiente sesión del usuario.
   */
  useEffect(() => {
    if (activeFramework) {
      localStorage.setItem('acsgc_active_framework', activeFramework.id);
    }
  }, [activeFramework]);

  /**
   * Restaura la selección de framework desde localStorage SOLO después de
   * que los frameworks estén cargados y el framework inicial esté establecido.
   *
   * IMPORTANTE: El framework inicial siempre será ISO 27002, pero respetamos
   * cambios posteriores del usuario guardados en localStorage.
   * Se excluye `activeFramework` de las dependencias para evitar un bucle infinito
   * de re-renders cuando el usuario cambia el framework activo.
   */
  useEffect(() => {
    if (frameworks.length > 0 && activeFramework) {
      const savedFrameworkId = localStorage.getItem('acsgc_active_framework');
      if (savedFrameworkId && savedFrameworkId !== activeFramework.id) {
        const savedFw = frameworks.find(fw => fw.id === savedFrameworkId);
        if (savedFw) {
          setActiveFrameworkState(savedFw);
          logger.info(`[FRAMEWORK] Restaurado desde localStorage: ${savedFw.name}`);
        }
      }
    }
  }, [frameworks]); // Removido activeFramework de dependencias para evitar loop

  /**
   * Cambia el framework activo buscándolo por ID en la lista cargada.
   * El cambio se persiste automáticamente en localStorage via el efecto correspondiente.
   *
   * @param frameworkId - UUID del framework a activar.
   */
  const setActiveFramework = useCallback((frameworkId: string) => {
    const framework = frameworks.find(fw => fw.id === frameworkId);
    if (framework) {
      setActiveFrameworkState(framework);
      logger.info(`[FRAMEWORK] Cambiado a: ${framework.name}`);
    } else {
      logger.warn(`[FRAMEWORK] Framework no encontrado: ${frameworkId}`);
    }
  }, [frameworks]);

  /**
   * Busca un framework en la lista cargada usando su código corto.
   *
   * @param code - Código del framework, ej. 'ISO27002', 'LMCS', 'CIS'.
   * @returns El framework encontrado, o `undefined` si no existe.
   */
  const getFrameworkByCode = useCallback((code: string) => {
    return frameworks.find(fw => fw.code === code);
  }, [frameworks]);

  /**
   * Fuerza una recarga de los frameworks desde Supabase.
   * Útil para reflejar cambios realizados en la BD sin recargar la página.
   */
  const refreshFrameworks = useCallback(async () => {
    await loadFrameworks();
  }, [loadFrameworks]);

  const value: FrameworkContextType = {
    frameworks,
    activeFramework,
    isLoading,
    error,
    isDemoMode,
    setActiveFramework,
    refreshFrameworks,
    getFrameworkByCode
  };

  return (
    <FrameworkContext.Provider value={value}>
      {children}
    </FrameworkContext.Provider>
  );
}

// =====================================================
// HOOK PERSONALIZADO
// =====================================================

/**
 * Hook para consumir el contexto de frameworks normativos.
 *
 * @throws Error si se usa fuera de `FrameworkProvider`.
 * @returns Estado completo del contexto de frameworks.
 *
 * @example
 * const { activeFramework, setActiveFramework } = useFramework();
 * // Cambiar al framework CIS Controls
 * const cis = frameworks.find(fw => fw.code === 'CIS');
 * if (cis) setActiveFramework(cis.id);
 */
export function useFramework(): FrameworkContextType {
  const context = useContext(FrameworkContext);
  if (context === undefined) {
    throw new Error('useFramework debe usarse dentro de un FrameworkProvider');
  }
  return context;
}

export default FrameworkContext;
